import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Rocket, CheckCircle2, ArrowRight, EyeOff, PartyPopper } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export const OnboardingGuide = ({ onDashboard = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/onboarding/status`);
      setData(res.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setHidden = async (hidden) => {
    try {
      await axios.post(`${API}/api/onboarding/hide`, { hidden });
      setData((d) => ({ ...d, hidden }));
      if (hidden) toast.success("Guía ocultada. Puedes volver a abrirla desde «Empieza aquí» en el menú.");
    } catch (e) {
      toast.error("No se pudo guardar");
    }
  };

  const toggleManual = async (step) => {
    try {
      await axios.post(`${API}/api/onboarding/manual`, { step_id: step.id, done: !step.manual_done });
      load();
    } catch (e) {
      toast.error("No se pudo guardar");
    }
  };

  if (user?.role === "super_admin") return null;
  if (loading || !data || data.steps.length === 0) return null;
  if (onDashboard && data.hidden) return null;

  const pct = data.total_count > 0 ? Math.round((data.completed_count / data.total_count) * 100) : 0;

  return (
    <Card className="border-indigo-200 dark:border-indigo-900 bg-gradient-to-br from-indigo-50/60 to-white dark:from-slate-800 dark:to-slate-800" data-testid="onboarding-guide">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Rocket className="h-5 w-5 text-indigo-600" />
            Empieza aquí
          </CardTitle>
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-slate-600 dark:text-slate-300" data-testid="onboarding-progress-text">
              {data.completed_count} de {data.total_count} completado
            </span>
            <Button variant="ghost" size="sm" onClick={() => setHidden(!data.hidden)} data-testid="onboarding-hide-btn">
              <EyeOff className="h-4 w-4 mr-1" />
              {data.hidden ? "Mostrar guía" : "Ocultar guía"}
            </Button>
          </div>
        </div>
        <div className="w-full h-2 rounded-full bg-slate-200 dark:bg-slate-700 mt-2" data-testid="onboarding-progress-bar">
          <div className="h-2 rounded-full bg-indigo-600 transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
      </CardHeader>
      <CardContent className="space-y-1">
        {data.all_completed && (
          <div className="flex items-center gap-3 p-4 mb-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800" data-testid="onboarding-congrats">
            <PartyPopper className="h-6 w-6 text-emerald-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-emerald-800 dark:text-emerald-300">¡Enhorabuena! Has completado la puesta en marcha.</p>
              <p className="text-sm text-emerald-700 dark:text-emerald-400">Tu tienda está lista para trabajar a pleno rendimiento.</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setHidden(true)}>Ocultar guía</Button>
          </div>
        )}
        {data.steps.map((step) => (
          <div
            key={step.id}
            className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${step.completed ? "opacity-70" : "hover:bg-white dark:hover:bg-slate-700/50"}`}
            data-testid={`onboarding-step-${step.id}`}
          >
            {step.completed ? (
              <CheckCircle2 className="h-7 w-7 text-emerald-500 flex-shrink-0" data-testid={`onboarding-step-done-${step.id}`} />
            ) : (
              <span className="h-7 w-7 rounded-full border-2 border-indigo-300 text-indigo-600 text-sm font-bold flex items-center justify-center flex-shrink-0">
                {step.num}
              </span>
            )}
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold ${step.completed ? "line-through text-slate-500" : "text-slate-800 dark:text-slate-200"}`}>
                {step.title}{step.optional ? " (opcional)" : ""}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{step.help}</p>
            </div>
            {step.manual && !step.completed && (
              <Button variant="ghost" size="sm" className="text-xs" onClick={() => toggleManual(step)} data-testid={`onboarding-manual-${step.id}`}>
                Marcar hecho
              </Button>
            )}
            {step.manual && step.manual_done && (
              <Button variant="ghost" size="sm" className="text-xs text-slate-400" onClick={() => toggleManual(step)}>
                Desmarcar
              </Button>
            )}
            {!step.completed && (
              <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => navigate(step.route)} data-testid={`onboarding-go-${step.id}`}>
                Ir <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default OnboardingGuide;
